// src/components/configuration/PositionsConfigPage.jsx
import React, { useEffect, useState, useContext } from 'react';
import {
  Box, Typography, Button, Table, TableHead, TableRow, TableCell, TableBody,
  Dialog, DialogTitle, DialogContent, TextField, DialogActions, IconButton,
  Select, MenuItem, InputLabel, FormControl, Divider
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import BlockIcon from '@mui/icons-material/Block';
import axios from '../../config/axiosInstance';
import { UserContext } from '../context/UserContext';
import useConfirm from '../calendar/dialogs/UseConfirm';
import { useSnackbar } from 'notistack';

export default function PositionsConfigPage() {
  const { enqueueSnackbar } = useSnackbar();
  const { confirm, ConfirmDialog } = useConfirm();
  const { permissions } = useContext(UserContext);
  const canRead = permissions.includes('stations.read') || permissions.includes('stations.read_all');
  const canWrite = permissions.includes('stations.write');

  // --- Áreas (tabla `position` de la base: Mini, Pista) ---
  const [positions, setPositions] = useState([]);
  const [open, setOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);

  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [editingPosition, setEditingPosition] = useState(null);

  const [loading, setLoading] = useState(false);

  const fetchPositions = async () => {
    setLoading(true);
    try {
      const res = await axios.get('position/list');
      setPositions(res.data?.positions || []);
    } catch (error) {
      console.error('Error al listar áreas:', error);
      setPositions([]);
    }
    setLoading(false);
  };

  useEffect(() => { if (canRead) fetchPositions(); }, [canRead]);

  const handleAdd = async () => {
    if (!name.trim()) return;
    try {
      await axios.post('position/add', { name: name.trim(), description: description.trim() || null, enabled: true });
      setOpen(false); setName(''); setDescription('');
      await fetchPositions();
    } catch (e) { console.error(e); enqueueSnackbar(e?.response?.data?.error || 'Error al crear el área', { variant: 'error' }); }
  };

  const handleEdit = (pos) => {
    setEditingPosition({ ...pos, enabled: typeof pos.enabled === 'boolean' ? pos.enabled : true });
    setName(pos.name || ''); setDescription(pos.description || '');
    setEditOpen(true);
  };

  const handleEditSave = async () => {
    if (!name.trim()) return;
    try {
      await axios.put('position/edit', {
        id: editingPosition.id,
        name: name.trim(),
        description: description.trim() || null,
        enabled: editingPosition.enabled,
      });
      setEditOpen(false); setEditingPosition(null);
      await fetchPositions();
    } catch (e) { console.error(e); enqueueSnackbar(e?.response?.data?.error || 'Error al editar el área', { variant: 'error' }); }
  };

  const handleDisable = async (pos) => {
    const ok = await confirm({
      title: 'Deshabilitar área',
      content: `¿Seguro que querés deshabilitar el área "${pos.name}"?`,
      confirmText: 'Deshabilitar',
    });
    if (!ok) return;
    try { await axios.delete('position/delete', { data: { id: pos.id } }); await fetchPositions(); }
    catch { enqueueSnackbar('Error al deshabilitar el área', { variant: 'error' }); }
  };

  const sortedPositions = [...positions].sort((a, b) => (a.enabled === b.enabled ? 0 : a.enabled ? -1 : 1));

  // --- Posiciones (tabla `workarea` de la base: Caja, Reponedor, Pista) ---
  const [workareas, setWorkareas] = useState([]);
  const [waLoading, setWaLoading] = useState(false);
  const [waOpen, setWaOpen] = useState(false);
  const [waEditOpen, setWaEditOpen] = useState(false);

  const [waNameArea, setWaNameArea] = useState('');
  const [waIdPosition, setWaIdPosition] = useState('');
  const [editingWorkarea, setEditingWorkarea] = useState(null);

  const fetchWorkareas = async () => {
    setWaLoading(true);
    try {
      const res = await axios.get('workarea/list');
      setWorkareas(res.data?.workareas || []);
    } catch (error) {
      console.error('Error al listar posiciones:', error);
      setWorkareas([]);
    }
    setWaLoading(false);
  };

  useEffect(() => { if (canRead) fetchWorkareas(); }, [canRead]);

  // Solo áreas habilitadas pueden recibir posiciones nuevas.
  const enabledPositions = positions.filter((pos) => pos.enabled !== false);

  // En edición, si la posición ya estaba en un área deshabilitada, la dejamos
  // visible en el selector (marcada) para no romper el dato existente.
  const editSelectablePositions = editingWorkarea && !enabledPositions.some((pos) => pos.id === editingWorkarea.id_position)
    ? [...enabledPositions, ...positions.filter((pos) => pos.id === editingWorkarea.id_position)]
    : enabledPositions;

  const openAddWorkarea = () => {
    setWaNameArea('');
    setWaIdPosition(''); // sin default: hay que elegir el área a propósito
    setWaOpen(true);
  };

  const handleAddWorkarea = async () => {
    if (!waNameArea.trim() || !waIdPosition) return;
    try {
      await axios.post('workarea/add', { name_area: waNameArea.trim(), id_position: waIdPosition });
      setWaOpen(false); setWaNameArea(''); setWaIdPosition('');
      await fetchWorkareas();
    } catch (e) {
      console.error(e);
      enqueueSnackbar(e?.response?.data?.error || 'Error al crear la posición', { variant: 'error' });
    }
  };

  const handleEditWorkarea = (wa) => {
    setEditingWorkarea(wa);
    setWaNameArea(wa.name_area || '');
    setWaIdPosition(wa.id_position ?? '');
    setWaEditOpen(true);
  };

  const handleEditWorkareaSave = async () => {
    if (!waNameArea.trim() || !waIdPosition) return;
    try {
      await axios.put('workarea/edit', {
        id: editingWorkarea.id,
        name_area: waNameArea.trim(),
        id_position: waIdPosition,
      });
      setWaEditOpen(false); setEditingWorkarea(null);
      await fetchWorkareas();
    } catch (e) {
      console.error(e);
      enqueueSnackbar(e?.response?.data?.error || 'Error al editar la posición', { variant: 'error' });
    }
  };

  const handleDeleteWorkarea = async (wa) => {
    const ok = await confirm({
      title: 'Eliminar posición',
      content: `¿Seguro que querés eliminar la posición "${wa.name_area}"? Esta acción no se puede deshacer.`,
      confirmText: 'Eliminar',
    });
    if (!ok) return;
    try {
      await axios.delete('workarea/delete', { data: { id: wa.id } });
      await fetchWorkareas();
    } catch (e) {
      console.error(e);
      enqueueSnackbar(e?.response?.data?.error || 'No se pudo eliminar la posición', { variant: 'error' });
    }
  };

  if (!canRead) {
    return (
      <Box p={4}><Typography variant="h6" color="error">No tienes permiso para ver esta sección.</Typography></Box>
    );
  }

  return (
    <Box sx={{ p: 4 }}>
      {ConfirmDialog}
      <Typography variant="h4" align="center" gutterBottom>Gestión de Áreas</Typography>

      {canWrite && (
        <Box display="flex" justifyContent="flex-end" mb={2}>
          <Button variant="contained" startIcon={<AddIcon />} onClick={() => setOpen(true)}>Agregar área</Button>
        </Box>
      )}

      <Box display="flex" justifyContent="center">
        <Box width="100%" maxWidth={800}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>ID</TableCell>
                <TableCell>Nombre</TableCell>
                <TableCell>Descripción</TableCell>
                <TableCell>Estado</TableCell>
                <TableCell align="right">Acciones</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {!loading && sortedPositions.map((pos) => {
                const muted = pos.enabled === false ? { color: '#aaa', textDecoration: 'line-through' } : {};
                return (
                  <TableRow key={pos.id}>
                    <TableCell sx={muted}>{pos.id}</TableCell>
                    <TableCell sx={muted}>{pos.name}</TableCell>
                    <TableCell sx={muted}>{pos.description}</TableCell>
                    <TableCell>
                      {pos.enabled ? (
                        <span style={{ color: '#36a16a', fontWeight: 600 }}>Habilitada</span>
                      ) : (
                        <span style={{ color: '#aaa', fontWeight: 600 }}>Deshabilitada</span>
                      )}
                    </TableCell>
                    <TableCell align="right">
                      <IconButton size="small" onClick={() => handleEdit(pos)} disabled={!canWrite || !pos.enabled}>
                        <EditIcon />
                      </IconButton>
                      <IconButton size="small" title="Deshabilitar" onClick={() => handleDisable(pos)} disabled={!canWrite || !pos.enabled}>
                        <BlockIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </Box>
      </Box>

      <Divider sx={{ my: 5 }} />

      <Typography variant="h4" align="center" gutterBottom>Gestión de Posiciones</Typography>

      {canWrite && (
        <Box display="flex" justifyContent="flex-end" mb={2}>
          <Button variant="contained" startIcon={<AddIcon />} onClick={openAddWorkarea} disabled={!enabledPositions.length}>
            Agregar posición
          </Button>
        </Box>
      )}

      <Box display="flex" justifyContent="center">
        <Box width="100%" maxWidth={800}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>ID</TableCell>
                <TableCell>Nombre</TableCell>
                <TableCell>Área</TableCell>
                <TableCell align="right">Acciones</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {!waLoading && workareas.map((wa) => (
                <TableRow key={wa.id}>
                  <TableCell>{wa.id}</TableCell>
                  <TableCell>{wa.name_area}</TableCell>
                  <TableCell>{wa.position_name || `Área ${wa.id_position}`}</TableCell>
                  <TableCell align="right">
                    <IconButton size="small" onClick={() => handleEditWorkarea(wa)} disabled={!canWrite}>
                      <EditIcon />
                    </IconButton>
                    <IconButton size="small" onClick={() => handleDeleteWorkarea(wa)} disabled={!canWrite}>
                      <DeleteIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Box>
      </Box>

      {/* Modal Agregar Área */}
      <Dialog open={open} onClose={() => setOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>Nueva área</DialogTitle>
        <DialogContent>
          <TextField margin="dense" label="Nombre" fullWidth value={name} onChange={(e) => setName(e.target.value)} />
          <TextField margin="dense" label="Descripción" fullWidth value={description} onChange={(e) => setDescription(e.target.value)} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)}>Cancelar</Button>
          <Button onClick={handleAdd} variant="contained" disabled={!name.trim()}>Crear</Button>
        </DialogActions>
      </Dialog>

      {/* Modal Editar Área */}
      <Dialog open={editOpen} onClose={() => setEditOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>Editar área</DialogTitle>
        <DialogContent>
          <TextField margin="dense" label="Nombre" fullWidth value={name} onChange={(e) => setName(e.target.value)} />
          <TextField margin="dense" label="Descripción" fullWidth value={description} onChange={(e) => setDescription(e.target.value)} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditOpen(false)}>Cancelar</Button>
          <Button onClick={handleEditSave} variant="contained">Guardar</Button>
        </DialogActions>
      </Dialog>

      {/* Modal Agregar Posición */}
      <Dialog open={waOpen} onClose={() => setWaOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>Nueva posición</DialogTitle>
        <DialogContent>
          <TextField margin="dense" label="Nombre" fullWidth value={waNameArea} onChange={(e) => setWaNameArea(e.target.value)} />
          <FormControl fullWidth margin="dense">
            <InputLabel id="wa-add-position-label">Área</InputLabel>
            <Select
              labelId="wa-add-position-label"
              label="Área"
              value={waIdPosition}
              onChange={(e) => setWaIdPosition(e.target.value)}
            >
              {enabledPositions.map((pos) => (
                <MenuItem key={pos.id} value={pos.id}>{pos.name}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setWaOpen(false)}>Cancelar</Button>
          <Button onClick={handleAddWorkarea} variant="contained" disabled={!waNameArea.trim() || !waIdPosition}>Crear</Button>
        </DialogActions>
      </Dialog>

      {/* Modal Editar Posición */}
      <Dialog open={waEditOpen} onClose={() => setWaEditOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>Editar posición</DialogTitle>
        <DialogContent>
          <TextField margin="dense" label="Nombre" fullWidth value={waNameArea} onChange={(e) => setWaNameArea(e.target.value)} />
          <FormControl fullWidth margin="dense">
            <InputLabel id="wa-edit-position-label">Área</InputLabel>
            <Select
              labelId="wa-edit-position-label"
              label="Área"
              value={waIdPosition}
              onChange={(e) => setWaIdPosition(e.target.value)}
            >
              {editSelectablePositions.map((pos) => (
                <MenuItem key={pos.id} value={pos.id}>
                  {pos.name}{pos.enabled === false ? ' (deshabilitada)' : ''}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setWaEditOpen(false)}>Cancelar</Button>
          <Button onClick={handleEditWorkareaSave} variant="contained">Guardar</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
